import z from "zod";
import { FeatureTypeEnum, SystemRoleEnum, UserStatusEnum } from "@/types/enum";
import { BuildingType, RoofType } from "@/types/building";

export const FeatureDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  config: z.object({
    color: z.string().optional(),
    visible: z.boolean().optional().default(true),
    isChecked: z.boolean().optional().default(true),
    isLocked: z.boolean().optional().default(false),
  }),
  properties: z.any(),
});

export const UserContentSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  systemRole: z.nativeEnum(SystemRoleEnum), // Add system role
  status: z.nativeEnum(UserStatusEnum), // Add user status
  createdAt: z.string(),
  lastLoginAt: z.string().optional(),
});

export const ProjectContentSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  clearanceDistance: z.number().optional().default(3),
  createdAt: z.string(),
  updatedAt: z.string(),
  datasetIds: z.array(z.string()).optional().default([]),
});

export const FeaturePolygonDataSchema = FeatureDataSchema.extend({
  coordinates: z.array(z.array(z.number())),
  properties: z.object({
    height: z.number().optional().default(10),
    grz: z.number().optional().default(0),
    bmz: z.number().optional().default(0),
    restrictionHeight: z.number().optional().default(10),
    linkedBuildingIds: z.array(z.string()).optional().default([]),
    isViolatingRestrictions: z.boolean().optional().default(false),
    offsetDistance: z.number().optional().default(0),
    offsetCoordinates: z.array(z.array(z.number())).optional().default([]),
    isManualOffset: z.boolean().optional().default(false),
    factor: z.number().optional().default(0),
    showSegmentLabels: z.boolean().optional().default(true),
    // Clearance area settings (independent from offset line)
    clearanceDistance: z.number().optional().default(0),
    showClearanceArea: z.boolean().optional().default(false),
    coverageRatio: z.number().optional().default(0.6),
    shadingLosses: z.number().optional().default(0.1),
    systemEfficiency: z.number().optional().default(0.15),
    energyYear: z.number().optional().default(0),
    orientationFactor: z.number().optional().default(0),
    sunHours: z.number().optional().default(0),
  }),
});

export const FeatureBuildingDataSchema = FeatureDataSchema.extend({
  coordinates: z.array(z.array(z.number())),
  properties: z.object({
    buildingType: z.nativeEnum(BuildingType),
    roofType: z.nativeEnum(RoofType),
    rotation: z.number().optional().default(0),
    width: z.number().optional().default(0),
    height: z.number().optional().default(0),
    length: z.number().optional().default(0),
    roofAngleDeg: z.number().optional().default(20),
    clearanceDistance: z.number().optional().default(0),
    showClearanceArea: z.boolean().optional().default(false),
    factor: z.number().optional().default(0),
    coverageRatio: z.number().optional().default(0.6),
    shadingLosses: z.number().optional().default(0.1),
    systemEfficiency: z.number().optional().default(0.15),
    energyYear: z.number().optional().default(0),
    orientationFactor: z.number().optional().default(0),
    sunHours: z.number().optional().default(0),
  }),
});

export const FeatureMeasureLengthDataSchema = FeatureDataSchema.extend({
  coordinates: z.array(z.array(z.number())),
  properties: z.object({
    length: z.number(),
    unit: z.string().default("m"),
  }),
});

export const FeatureMeasureAreaDataSchema = FeatureDataSchema.extend({
  coordinates: z.array(z.array(z.number())),
  properties: z.object({
    area: z.number(),
    unit: z.string().default("m²"),
  }),
});

export const FeatureTileOverlayDataSchema = FeatureDataSchema.extend({
  coordinates: z.array(z.array(z.number())),
  properties: z.object({
    minZoom: z.number(),
    maxZoom: z.number(),
    bounds: z.array(z.number()),
    isProcessed: z.boolean().optional().default(false),
    rotation: z.number().optional().default(0),
    position: z.array(z.number()),
    scale: z.number(),
  }),
  config: FeatureDataSchema.shape.config.extend({
    opacity: z.number().optional().default(0.5),
  }),
});
